import { EmailIcon, PhoneIcon } from '@chakra-ui/icons';
import {
  Button,
  Flex,
  Heading,
  IconButton,
  Link,
  Td,
  Text,
  Th,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { useBuyersForCurrentAgentPaginatedQuery } from '@client/graphql/__generated__/main-operations';
import { FC, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { BasicCardTable } from '~/common/components/BasicCardTable/BasicCardTable';
import { useInfiniteQuery } from '~/services/document/hooks';
import { AddBuyerModal } from '../../components/Buyer/AddBuyerModal/AddBuyerModal';
import { Container } from '../../components/Layouts/Container';
import { MOBILE_HEADER_HEIGHT } from '../../components/Layouts/MobileHeader';
import { SIDEBAR_WIDTH_COLLAPSED } from '../../components/PageDetailLayout/SidebarItem';
import { UserAvatar } from '../../components/User/UserAvatar';
import { getPartyEmail, getPartyPhoneNumber } from '../../utils/party.utils';

export const BuyersPage: FC = () => {
  const [fetchBuyerClientsTriggerRef, { data, loading, refetch }] =
    useInfiniteQuery(useBuyersForCurrentAgentPaginatedQuery);

  const navigate = useNavigate();
  const handleClickRow = useCallback(
    (buyerPartyId: string) => {
      navigate(`/buyers/${buyerPartyId}`);
    },
    [navigate]
  );
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Container
      height={{ base: `calc(100vh - ${MOBILE_HEADER_HEIGHT})`, md: '100vh' }}
      minHeight={0}
      width={`calc(100vw - ${SIDEBAR_WIDTH_COLLAPSED})`}
    >
      <Flex direction="column" flex={1} height="100%" minHeight={0}>
        <Flex justifyContent="space-between" mb={5}>
          <Heading
            as="h2"
            display={{ base: 'none', md: 'block' }}
            size="sm"
            whiteSpace="normal"
          >
            Buyers
          </Heading>

          <Button size="xs" onClick={onOpen}>
            Add a buyer
          </Button>
        </Flex>

        <BasicCardTable
          body={
            <>
              {data?.buyersForCurrentAgentPaginated?.results?.map((party) => {
                const latestBuyerOfferAnalysis = party.latestBuyerOfferAnalysis;
                const consumers = [
                  party.primaryConsumer,
                  ...party.secondaryConsumers,
                ];
                const primaryConsumer = party.primaryConsumer;
                const phoneNumber = getPartyPhoneNumber(party);
                const email = getPartyEmail(party);

                return (
                  <Tr
                    key={party.id}
                    _hover={{ cursor: 'pointer' }}
                    height={{ base: '60px', md: 'auto' }}
                    onClick={() => handleClickRow(party.id)}
                  >
                    <Td flex={2}>
                      <Flex alignItems="center">
                        <UserAvatar mr={3} size="sm" user={primaryConsumer} />
                        <Text fontWeight="bold">
                          {consumers.map((consumer, index) => (
                            <span key={consumer.id}>
                              {index > 0 ? ', ' : ''}
                              {consumer.fullName}
                            </span>
                          ))}
                        </Text>
                      </Flex>
                    </Td>
                    <Td flex={1}>
                      {latestBuyerOfferAnalysis?.listing?.property
                        ?.streetAddress ? (
                        <Button
                          variant="link"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/offer-scorecard/${latestBuyerOfferAnalysis?.id}`
                            );
                          }}
                        >
                          {
                            latestBuyerOfferAnalysis?.listing?.property
                              ?.streetAddress
                          }
                        </Button>
                      ) : (
                        <Text color="whiteAlpha.600">N/A</Text>
                      )}
                    </Td>
                    <Td flex={1}>
                      <Flex>
                        {phoneNumber ? (
                          <Link isExternal href={`tel:${phoneNumber}`}>
                            <IconButton
                              aria-label="Contact by phone"
                              icon={<PhoneIcon />}
                              variant="ghost"
                            />
                          </Link>
                        ) : (
                          <IconButton
                            aria-label="Contact by phone"
                            icon={<PhoneIcon />}
                            isDisabled={true}
                            variant="ghost"
                          />
                        )}
                        {email ? (
                          <Link isExternal href={`mailto:${email}`}>
                            <IconButton
                              aria-label="Contact by email"
                              icon={<EmailIcon />}
                              variant="ghost"
                            />
                          </Link>
                        ) : (
                          <IconButton
                            aria-label="Contact by email"
                            icon={<EmailIcon />}
                            isDisabled={true}
                            variant="ghost"
                          />
                        )}
                      </Flex>
                    </Td>
                  </Tr>
                );
              })}
            </>
          }
          fetchTriggerRef={fetchBuyerClientsTriggerRef}
          header={
            <Tr>
              <Th flex={2}>Name</Th>
              <Th flex={1}>Latest Offer Scorecard</Th>
              <Th flex={1}>Contact</Th>
            </Tr>
          }
          loading={loading}
        />

        <AddBuyerModal isOpen={isOpen} refetch={refetch} onClose={onClose} />
      </Flex>
    </Container>
  );
};
